/**
 * One player of a live session (operators only, 2026-10-18): their clips over every segment, played
 * with the render options, the pitch map of where their balls pitched, and Export: one MP4 of the pitch
 * map, then each clip from its release to `post_s` seconds after it (default 3; the owner's "release + 3"
 * read as seconds, a parameter here), with the speeds and traces burned in when those boxes are ticked.
 *
 * Layout (owner, 2026-10-18): a two-column grid, 8 / 4. Row 1: the video (the clip's title above it, a
 * toolbar with the facts and the render options under it) and, at its right, the clip list, its height
 * locked to the video column and scrolling inside. Row 2: the pitch map (40%) beside the Export card
 * (60%), the same height. The header carries "Export video", which brings the Export card into view.
 * A zone of the pitch map, clicked, filters the clip list to the balls that pitched there.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { live, metricNum, zoneCode, type LiveClip, type LiveExportView } from "../api/live";
import { useLoad } from "../api/hooks";
import { useAuth } from "../auth/AuthContext";
import { LiveClipPlayer, RenderOptions, useRenderOptions } from "../components/LiveClipPlayer";
import { PitchMap, type MapBall } from "../components/PitchMap";
import { Shell } from "../components/Shell";
import { Bar, ErrorLine, Face } from "../components/ui";
import { prefetchClipUrls } from "../lib/clipPrefetch";
import { plural } from "../lib/format";
import { OperatorsOnly } from "./AdminPipeline";
import { clipFacts, useOverlays } from "./LiveSession";
import { liveDeliverySummary } from "../lib/descriptions";

/** A clip as a ball on the pitch map: its published zone, a full toss where the pipeline said so, else unseen. */
function mapBall(c: LiveClip, fullToss: boolean): MapBall {
  const z = zoneCode(c.metrics);
  if (z) return { zone: z, verdict: "placed" };
  return { zone: null, verdict: fullToss ? "full toss" : "not shown" };
}

/** The zone a ball sits in on the map: its code, "FT" for a full toss, null when no bounce was read. */
const zoneOf = (b: MapBall) => (b.verdict === "placed" ? b.zone : b.verdict === "full toss" ? "FT" : null);

export default function LivePlayer() {
  const { id = "", key = "" } = useParams();
  const { me } = useAuth();
  const pl = useLoad(() => live.player(id, key), [id, key]);
  const [render, setRender] = useRenderOptions();
  const [i, setI] = useState(0);
  const [autoNext, setAutoNext] = useState(true);
  const [zone, setZone] = useState<string | null>(null);
  const { byUrl, want } = useOverlays();
  const d = pl.data;
  const clips = useMemo(() => d?.clips ?? [], [d]);
  useEffect(() => {
    for (const c of clips) want(c.segment?.overlays ?? null, "1");
  }, [clips, want]);
  const balls = useMemo(() => clips.map((c) => {
    const doc = c.segment?.overlays ? byUrl[c.segment.overlays] : undefined;
    return mapBall(c, !!doc?.deliveries[c.candidate_id]?.zone?.full_toss);
  }), [clips, byUrl]);
  // the clip list, filtered by the zone clicked on the map
  const shown = useMemo(() => clips.map((c, k) => ({ c, b: balls[k]! })).filter((x) => !zone || zoneOf(x.b) === zone), [clips, balls, zone]);
  const curIdx = Math.min(i, Math.max(0, shown.length - 1));
  const cur = shown[curIdx]?.c ?? null;
  useEffect(() => {
    prefetchClipUrls([
      shown[curIdx + 1]?.c.playback_url,
      shown[curIdx + 2]?.c.playback_url,
      shown[curIdx - 1]?.c.playback_url,
    ]);
  }, [shown, curIdx]);
  const warmLiveClip = useCallback((k: number) => {
    if (k === curIdx) return;
    prefetchClipUrls([
      shown[k]?.c.playback_url,
      shown[curIdx + 1]?.c.playback_url,
      shown[curIdx + 2]?.c.playback_url,
      shown[curIdx - 1]?.c.playback_url,
    ]);
  }, [shown, curIdx]);
  const ovDoc = cur?.segment?.overlays ? byUrl[cur.segment.overlays] : undefined;
  const overlay = cur && ovDoc ? ovDoc.deliveries[cur.candidate_id] ?? null : null;
  const frame = ovDoc?.frame ?? { width: 1280, height: 720, fps: 29.97 };
  const next = useCallback(() => {
    if (autoNext) setI((k) => (k + 1 < shown.length ? k + 1 : k));
  }, [autoNext, shown.length]);
  const pickZone = (z: string | null) => {
    setZone((cur) => (cur === z ? null : z));
    setI(0);
  };

  // ---- export
  const [opts, setOpts] = useState({ post_s: 3, pre_s: 0, map_s: 4 });
  const [exp, setExp] = useState<{ id: string; view: LiveExportView | null } | null>(null);
  const [expErr, setExpErr] = useState<Error | null>(null);
  const [flash, setFlash] = useState(false);
  const exportRef = useRef<HTMLElement | null>(null);
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  if (me && !me.user.is_operator) return <OperatorsOnly shows="live match sessions" />;
  const fileName = (d?.player.name || d?.player.label || "player").replace(/[^A-Za-z0-9]+/g, "_").toLowerCase() + "_live";
  const startExport = async () => {
    setExpErr(null);
    try {
      const r = await live.exportPlayer(id, key, { speeds: render.speeds, traces: render.traces, ...opts });
      setExp({ id: r.export_id, view: null });
      const poll = async (n: number) => {
        const v = await live.exportView(r.export_id, fileName);
        setExp({ id: r.export_id, view: v });
        if (v.export.state === "requested" && n < 300) timer.current = window.setTimeout(() => void poll(n + 1), 2000);
      };
      await poll(0);
    } catch (e) {
      setExpErr(e as Error);
    }
  };
  const toExport = () => {
    exportRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlash(true);
    window.setTimeout(() => setFlash(false), 1400);
  };
  const p = d?.player;
  const state = exp?.view?.export.state ?? (exp ? "requested" : null);
  const idx = cur ? shown.findIndex((x) => x.c.id === cur.id) : -1;
  return (
    <Shell title={p ? p.name || p.label : "Player"} back={`/admin/live/${id}`}>
      <div className="p lv-pp">
        <header className="row between mt16 lv-pp-head" data-testid="live-player-head">
          <div className="row lv-phead">
            {p?.crop_url ? <Face id={p.key} src={p.crop_url} size={64} radius={8} /> : null}
            <div>
              <div className="tiny">{d?.session.title ?? ""}</div>
              <h1 data-testid="live-player-name">{p ? p.name || p.label : "…"}</h1>
              <div className="sub">{p ? `${p.role === "batsman" ? "Batsman" : "Bowler"} · ${plural(clips.length, "clip")}` : ""}</div>
            </div>
          </div>
          {clips.length > 0 && <button className="btn dark" onClick={toExport} data-testid="export-open">Export video</button>}
        </header>
        <ErrorLine error={pl.error} />
        {cur ? (
          <div className="lv-pp-top mt24">
            <div className="lv-pp-main" data-testid="live-stage">
              <div className="med lv-pp-title" data-testid="clip-title">
                Clip {cur.index} of {clips.length} · segment {cur.segment?.seq}{cur.bowler || cur.batsman ? ` · ${cur.bowler?.label ?? "?"} to ${cur.batsman?.label ?? "?"}` : ""}
              </div>
              <LiveClipPlayer src={cur.playback_url} overlay={overlay} frameSize={frame} fps={frame.fps} showSpeeds={render.speeds} showTraces={render.traces} autoPlay onEnded={next}
                label={`clip ${cur.index}`} description={liveDeliverySummary(cur)} />
              <div className="lv-toolbar">
                <div className="tiny" data-testid="clip-facts">{clipFacts(cur).join(" · ") || "No measurements for this clip."}</div>
                <div className="row wrap-r lv-toolbar-opts">
                  <RenderOptions speeds={render.speeds} traces={render.traces} onChange={setRender} />
                  <label className="lv-check"><input type="checkbox" checked={autoNext} onChange={(e) => setAutoNext(e.target.checked)} data-testid="opt-next" /><span>Play the next clip</span></label>
                </div>
              </div>
            </div>
            <aside className="lv-pp-list" aria-label="Clips">
              <div className="lv-pp-scroll" data-testid="player-clips">
                {zone && (
                  <div className="lv-filter" data-testid="zone-filter">
                    <span>Zone <b>{zone}</b> · {plural(shown.length, "clip")}</span>
                    <button className="u tiny" onClick={() => pickZone(null)}>Show all</button>
                  </div>
                )}
                {shown.map(({ c, b }, k) => {
                  const bowl = metricNum(c.metrics.bowl_speed) ?? metricNum(c.metrics.ball_speed_estimate);
                  const z = zoneOf(b);
                  return (
                    <button key={c.id} className={`lv-crow ${k === idx ? "on" : ""}`} onClick={() => setI(k)} onPointerEnter={() => warmLiveClip(k)} onTouchStart={() => warmLiveClip(k)} data-testid="clip" data-candidate={c.candidate_id}
                      title={`segment ${c.segment?.seq}`}>
                      <span className="n">Clip {c.index}<small>seg {c.segment?.seq}</small></span>
                      <span className={`pill-z ${z ? "" : "none"}`}>{z ?? "—"}</span>
                      <span className="v">{bowl != null ? <>{bowl}<small> km/h</small></> : <small>no speed</small>}</span>
                    </button>
                  );
                })}
              </div>
            </aside>
          </div>
        ) : (
          d && <p className="sub mt24">No clips {zone ? `in zone ${zone}` : "for this player"}.</p>
        )}
        {clips.length > 0 && (
          <div className="lv-pp-bottom mt24">
            <PitchMap balls={balls} title={`Where ${p?.role === "bowler" ? "their balls" : "the balls they faced"} pitched`} selected={zone} onZone={pickZone} />
            <section ref={exportRef} className={`card lv-export ${flash ? "flash" : ""}`} data-testid="live-export">
              <div className="h3">Export video</div>
              <p className="sub mt6">One video: the pitch map, then each clip from its release. Speeds and traces are drawn in when they are ticked under the video.</p>
              <div className="lv-export-opts mt10">
                <label className="lv-field"><span className="tiny">Seconds after the release</span><input className="field" type="number" min={0} max={20} step={0.5} value={opts.post_s} onChange={(e) => setOpts({ ...opts, post_s: Number(e.target.value) })} data-testid="export-post" /></label>
                <label className="lv-field"><span className="tiny">Seconds before the release</span><input className="field" type="number" min={0} max={10} step={0.5} value={opts.pre_s} onChange={(e) => setOpts({ ...opts, pre_s: Number(e.target.value) })} data-testid="export-pre" /></label>
                <label className="lv-field"><span className="tiny">Pitch map seconds</span><input className="field" type="number" min={0} max={20} step={0.5} value={opts.map_s} onChange={(e) => setOpts({ ...opts, map_s: Number(e.target.value) })} data-testid="export-map" /></label>
              </div>
              <div className="tiny mt10">{render.speeds ? "Speeds on" : "Speeds off"} · {render.traces ? "traces on" : "traces off"} · all {plural(clips.length, "clip")}</div>
              <button className="btn dark mt16" onClick={() => void startExport()} disabled={state === "requested"} data-testid="export-start">{state === "requested" ? "Preparing…" : "Export"}</button>
              {state === "requested" && <div className="mt10"><Bar value={0.5} /><div className="tiny mt6">Rendering on the live hub…</div></div>}
              {state === "failed" && <p className="tiny err mt10" data-testid="export-failed">The export failed{exp?.view?.job?.error ? `: ${exp.view.job.error.code}` : ""}.</p>}
              {state === "ready" && exp?.view?.download_url && (
                <div className="mt16" data-testid="export-ready">
                  <a className="btn" href={exp.view.download_url} download data-testid="export-download">Download {fileName}.mp4</a>
                  <video className="lv-export-preview mt10" src={exp.view.download_url} controls muted playsInline data-testid="export-preview" />
                </div>
              )}
              <ErrorLine error={expErr} />
            </section>
          </div>
        )}
      </div>
    </Shell>
  );
}
